import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Loader2, Wifi, WifiOff, Laptop, Tablet, Smartphone, HelpCircle, ShieldCheck, WifiOff as OfflineIcon, RefreshCw, PowerOff } from "lucide-react";
import { toast } from "sonner";
import { DiagnosticsCard, OutageList } from "./KioskDiagnostics";
import { CAUSE_META } from "./offlineCauses";

const WINDOW_DAYS = 14;
const CLIENT_ICON = { "iPad": Tablet, "Desktop browser": Laptop, "Mobile": Smartphone };
const ALERT_LABEL = { stage: "Offline alert", escalation: "Escalation", reminder: "Still-offline reminder", back_online: "Back online" };

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function dur(a, b) {
  const ms = new Date(b) - new Date(a);
  if (!(ms > 0)) return "";
  const m = Math.round(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function clientDetail(ua, ip, client) {
  if (!ua && !ip) return null;
  let browser = "Browser";
  if (/edg/i.test(ua)) browser = "Edge";
  else if (/chrome|crios/i.test(ua)) browser = "Chrome";
  else if (/firefox|fxios/i.test(ua)) browser = "Firefox";
  else if (/safari/i.test(ua)) browser = "Safari";
  let os = "";
  if (client === "iPad") os = "iPadOS";
  else if (/windows/i.test(ua)) os = "Windows";
  else if (/mac os x|macintosh/i.test(ua)) os = "macOS";
  else if (/iphone|ipad|ios/i.test(ua)) os = "iOS";
  else if (/android/i.test(ua)) os = "Android";
  else if (/linux|cros/i.test(ua)) os = "Linux";
  const parts = [];
  if (ua) parts.push(browser + (os ? ` on ${os}` : ""));
  if (ip) parts.push(`IP ${ip}`);
  return parts.join("  ·  ");
}

export function KioskHealth({ kioskId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = (silent) => {
    if (!silent) setLoading(true);
    api.get(`/kiosks/${kioskId}/health`).then((r) => setData(r.data)).catch(() => {}).finally(() => { if (!silent) setLoading(false); });
  };
  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, [kioskId]);

  const [closing, setClosing] = useState(false);
  const closeSession = async () => {
    setClosing(true);
    try {
      await api.post(`/kiosks/${kioskId}/close-session`);
      toast.success("Close signal sent — the open browser/tablet will lock and stop reporting within ~30s.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not close the session"); }
    finally { setClosing(false); }
  };

  if (loading && !data) return <div className="py-10 text-slate-400 text-sm" data-testid="kiosk-health-loading"><Loader2 className="h-4 w-4 animate-spin inline mr-1.5" /> Loading health…</div>;
  if (!data) return null;

  const now = Date.now();
  const start = now - WINDOW_DAYS * 86400000;
  const online = data.status === "online";
  const ClientIcon = CLIENT_ICON[data.last_client] || HelpCircle;

  const segs = (data.outages || []).map((o) => {
    const s = Math.max(new Date(o.start).getTime(), start);
    const e = Math.min(new Date(o.end).getTime(), now);
    if (e <= start) return null;
    return { left: ((s - start) / (now - start)) * 100, width: Math.max(0.6, ((e - s) / (now - start)) * 100), bar: (CAUSE_META[o.cause] || CAUSE_META.unknown).bar, title: `${(CAUSE_META[o.cause] || CAUSE_META.unknown).label} · ${dur(o.start, o.end)}` };
  }).filter(Boolean);
  const covered = (t) => (data.outages || []).some((o) => new Date(o.start).getTime() <= t + 60000 && new Date(o.end).getTime() >= t - 60000);
  (data.incidents || []).forEach((i) => {
    const o = Math.max(new Date(i.opened_at).getTime(), start);
    const e = Math.min(i.resolved_at ? new Date(i.resolved_at).getTime() : now, now);
    if (e <= start || o >= now || (i.resolved_at && covered(o))) return;
    segs.push({ left: ((o - start) / (now - start)) * 100, width: Math.max(0.6, ((e - o) / (now - start)) * 100), bar: i.severity === "critical" ? "bg-rose-500" : "bg-amber-400", title: i.resolved_at ? "Offline (before diagnostics)" : "Ongoing outage" });
  });
  const ongoingSecs = !online && data.last_seen ? (now - new Date(data.last_seen).getTime()) / 1000 : 0;
  if (ongoingSecs > 0 && !(data.incidents || []).some((i) => !i.resolved_at)) {
    const o = Math.max(new Date(data.last_seen).getTime(), start);
    segs.push({ left: ((o - start) / (now - start)) * 100, width: Math.max(0.6, ((now - o) / (now - start)) * 100), bar: "bg-rose-500", title: "Offline now" });
  }
  const usedCauses = [...new Set((data.outages || []).map((o) => o.cause))];

  return (
    <div className="space-y-5" data-testid="kiosk-panel-health">
      {/* Current status */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 flex items-start gap-3" data-testid="kiosk-health-status">
        <span className={`h-9 w-9 rounded-lg grid place-items-center shrink-0 ${online ? "bg-emerald-100 text-emerald-600" : "bg-rose-100 text-rose-600"}`}>
          {online ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900 dark:text-white">{online ? "Online" : "Offline"}</p>
          <p className="text-xs text-slate-500">Last heartbeat {fmt(data.last_seen)}</p>
          {data.last_client && (
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5" data-testid="kiosk-health-client">
              <ClientIcon className="h-3.5 w-3.5" /> Last seen on <b className="text-slate-700 dark:text-slate-200">{data.last_client}</b>
              {online && data.last_client === "Desktop browser" && <span className="text-amber-600">— a computer session is keeping this kiosk “online”.</span>}
            </p>
          )}
          {clientDetail(data.last_seen_ua, data.last_ip, data.last_client) && (
            <p className="text-[11px] text-slate-400 mt-0.5 pl-5" data-testid="kiosk-health-client-detail">Source: {clientDetail(data.last_seen_ua, data.last_ip, data.last_client)}</p>
          )}
        </div>
        <button data-testid="kiosk-health-refresh" onClick={load} className="text-slate-400 hover:text-indigo-600 p-1" title="Refresh"><RefreshCw className="h-4 w-4" /></button>
      </div>

      {/* Remote close session */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 flex items-start gap-3" data-testid="kiosk-health-close">
        <span className="h-9 w-9 rounded-lg grid place-items-center shrink-0 bg-rose-100 text-rose-600"><PowerOff className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900 dark:text-white">Close forgotten browser sessions</p>
          <p className="text-xs text-slate-500">Closes any PC/Mac or other <b>browser</b> session left open for this kiosk — it locks and stops reporting as online within ~30s. Your <b>kiosk tablet (iPad) is not affected</b> and keeps running, so it's safe to use even when the tablet is off-site.</p>
        </div>
        <button
          data-testid="kiosk-health-close-btn"
          onClick={closeSession}
          disabled={closing}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white px-3 py-2 text-sm font-semibold"
        >
          {closing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PowerOff className="h-4 w-4" /> Close session</>}
        </button>
      </div>

      <DiagnosticsCard data={data} />

      {/* Lifeline */}
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Last {WINDOW_DAYS} days — offline periods</p>
        <div className="relative h-8 rounded-lg bg-emerald-100 dark:bg-emerald-950/30 overflow-hidden border border-emerald-200/60 dark:border-emerald-900/40" data-testid="kiosk-health-lifeline">
          {segs.map((s, i) => (
            <div key={i} className={`absolute top-0 h-full ${s.bar}`} style={{ left: `${s.left}%`, width: `${s.width}%` }} title={s.title} />
          ))}
        </div>
        <div className="flex justify-between text-[10px] text-slate-400 mt-1">
          <span>{new Date(start).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}</span>
          <span>Now</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[11px] text-slate-500">
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-400 inline-block" /> Online</span>
          {usedCauses.map((c) => <span key={c} className="flex items-center gap-1"><span className={`h-2.5 w-2.5 rounded-sm inline-block ${(CAUSE_META[c] || CAUSE_META.unknown).bar}`} /> {(CAUSE_META[c] || CAUSE_META.unknown).label}</span>)}
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-amber-400 inline-block" /> Offline (warning)</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-rose-500 inline-block" /> Offline (critical)</span>
        </div>
        {!segs.length && <p className="text-xs text-slate-400 mt-2">No offline periods recorded in this window.</p>}
      </div>

      <OutageList outages={data.outages || []} ongoing={ongoingSecs > 0 ? ongoingSecs : 0} />

      {/* Alert history */}
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Alert history — cross-check against your emails</p>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800 overflow-hidden" data-testid="kiosk-health-alerts">
          {(data.alerts || []).length ? data.alerts.map((a, i) => {
            const back = a.kind === "back_online";
            return (
              <div key={i} className="flex items-center gap-3 px-3 py-2" data-testid={`kiosk-health-alert-${i}`}>
                <span className={`h-6 w-6 rounded grid place-items-center shrink-0 ${back ? "bg-emerald-100 text-emerald-600" : "bg-rose-100 text-rose-600"}`}>
                  {back ? <ShieldCheck className="h-3.5 w-3.5" /> : <OfflineIcon className="h-3.5 w-3.5" />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-800 dark:text-slate-200">{ALERT_LABEL[a.kind] || a.kind}{a.minutes != null ? ` · after ${a.minutes}m` : ""}{a.downtime ? ` · down ${a.downtime}` : ""}{a.combined ? " · combined alert" : ""}</p>
                  {a.cause_label && <p className="text-[11px] text-slate-600 dark:text-slate-300" data-testid={`kiosk-health-alert-cause-${i}`}>Likely cause: {a.cause_label}</p>}
                  <p className="text-[11px] text-slate-400">{fmt(a.at)} · emailed {a.recipients ?? 0} recipient(s)</p>
                </div>
              </div>
            );
          }) : <p className="px-3 py-4 text-xs text-slate-400">No alerts have ever fired for this kiosk.</p>}
        </div>
      </div>
    </div>
  );
}
